import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Award, Check, CheckCircle2, Circle, Hexagon, BookOpen, Trophy, type LucideIcon } from 'lucide-react'
import { cn } from '../../../app/libs/cn'
import { getMySkills, type MySkill, type MySkillsData } from '@/services/skillService'
import { CATEGORY_PALETTE, SKILL_ICON_MAP, getSkillIcon } from './skillIcons'

// หน้า "สกิลของฉัน" — ข้อมูลจริงจาก GET /api/my-skills (server คำนวณ x/y จากการเรียนจบจริง)
// กติกา: หมวดสกิล = หมวดหมู่คอร์ส · สกิลย่อย = กลุ่มคอร์ส · เรียนจบคอร์สในสกิลย่อย 1 คอร์สได้ 1 แต้ม (x / y)
// ครบทุกคอร์สในสกิลย่อย = ได้รับสกิลนั้น · คอร์สใหม่เพิ่มเข้าสกิลย่อย ตัวหาร y เพิ่มขึ้นเอง (เช่น 5/5 → 5/6)

const HEX_CLIP = 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)'

interface CategoryColor { from: string; to: string }

// สกิลที่ "ได้รับแล้ว" ยังนับว่าสำเร็จ แม้มีคอร์สใหม่ทำให้ x/y ยังไม่ครบ (ได้แล้วไม่หาย)
const progressOf = (sub: MySkill) => {
  const { done, total } = sub
  return { done, total, complete: sub.earned, started: sub.started }
}

function HexBadge({ Icon, from, to, state, selected }: {
  Icon: LucideIcon; from: string; to: string; state: 'locked' | 'progress' | 'complete'; selected: boolean
}) {
  const gradient = `linear-gradient(145deg, ${from}, ${to})`
  return (
    <div className="relative h-[84px] w-[76px] transition-transform duration-200 group-hover:-translate-y-0.5">
      {/* วงเรืองแสงเมื่อถูกเลือก */}
      {selected && <div className="absolute -inset-[6px] opacity-25" style={{ clipPath: HEX_CLIP, background: gradient }} />}
      <div className="absolute inset-0" style={{ clipPath: HEX_CLIP, background: state === 'locked' ? '#EEECF8' : gradient }} />
      {/* ขอบใน ให้เหรียญดูมีมิติ */}
      {state !== 'locked' && (
        <div className="absolute inset-[3px] opacity-60" style={{ clipPath: HEX_CLIP, background: 'linear-gradient(160deg, rgba(255,255,255,0.35), rgba(255,255,255,0) 55%)' }} />
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className={cn('h-8 w-8', state === 'locked' ? 'text-[#A7A3C9]' : 'text-white')} strokeWidth={1.6} />
      </div>
      {state === 'complete' && (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      )}
    </div>
  )
}

function Segments({ done, total, color }: { done: number; total: number; color: string }) {
  // คอร์สไม่เกิน 10 → แบ่งเป็นช่องตามจำนวนคอร์ส (เห็นชัดว่าเหลืออีกกี่คอร์ส) / มากกว่านั้นใช้แถบต่อเนื่อง
  if (total > 10) {
    return (
      <div className="mx-auto mt-1.5 h-1.5 w-16 overflow-hidden rounded-full bg-[#E7E5FF]">
        <div className="h-full rounded-full" style={{ width: `${(done / total) * 100}%`, background: color }} />
      </div>
    )
  }
  return (
    <div className="mx-auto mt-1.5 flex h-1.5 w-16 gap-0.5">
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className="h-full flex-1 rounded-full" style={{ background: i < done ? color : '#E7E5FF' }} />
      ))}
    </div>
  )
}

function SubSkillCard({ sub, category, selected, onSelect }: {
  sub: MySkill; category: CategoryColor; selected: boolean; onSelect: () => void
}) {
  const { done, total, complete, started } = progressOf(sub)
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'group flex w-[136px] flex-col items-center rounded-xl border px-2 pb-3 pt-4 text-center transition-all',
        selected ? 'border-accent/40 bg-[#F6F5FF] shadow-[0_10px_24px_-16px_rgba(108,99,255,0.6)]' : 'border-transparent hover:border-[#E7E5FF] hover:bg-[#FBFAFF]',
      )}
    >
      <HexBadge Icon={getSkillIcon(sub.icon)} from={category.from} to={category.to} state={complete ? 'complete' : started ? 'progress' : 'locked'} selected={selected} />
      <p className="mt-3 line-clamp-2 min-h-[2.4rem] text-xs font-medium leading-snug text-[#19172A]">{sub.name}</p>
      <p className="mt-1 text-sm font-bold tabular-nums text-[#19172A]">
        {done} <span className="font-normal text-[#69738A]">/</span> {total}
      </p>
      <Segments done={done} total={total} color={complete ? '#10B981' : category.from} />
    </button>
  )
}

function SkillGlyph({ iconKey, className, strokeWidth }: { iconKey: string; className?: string; strokeWidth?: number }) {
  const Icon = SKILL_ICON_MAP[iconKey] ?? Award
  return <Icon className={className} strokeWidth={strokeWidth} />
}

function SkillDetail({ sub, category }: { sub: MySkill; category: CategoryColor }) {
  const { done, total, complete } = progressOf(sub)
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
  return (
    <div className="mt-5 animate-fade-in rounded-xl border border-[#E7E5FF] bg-[#FBFAFF] p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg text-white" style={{ background: `linear-gradient(145deg, ${category.from}, ${category.to})` }}>
            <SkillGlyph iconKey={sub.icon} className="h-5 w-5" strokeWidth={1.7} />
          </span>
          <div>
            <p className="text-[15px] font-semibold text-[#19172A]">{sub.name}</p>
            <p className="text-xs text-[#69738A]">เรียนจบแล้ว {done} จาก {total} คอร์ส</p>
          </div>
        </div>
        <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', complete ? 'bg-emerald-50 text-emerald-600' : 'bg-accent/10 text-accent')}>
          {complete ? 'ได้รับสกิลแล้ว' : `${percent}%`}
        </span>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#E7E5FF]">
        <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, background: complete ? '#10B981' : `linear-gradient(90deg, ${category.from}, ${category.to})` }} />
      </div>

      <ol className="mt-4 space-y-2">
        {sub.courses.map((course) => (
          <li key={course.course_id} className="flex items-center gap-3 rounded-lg border border-[#EEECF8] bg-white px-4 py-3">
            {course.done
              ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />
              : <Circle className="h-5 w-5 shrink-0 text-[#C9C5E8]" />}
            <span className={cn('min-w-0 flex-1 truncate text-sm', course.done ? 'text-[#69738A]' : 'font-medium text-[#19172A]')}>{course.title}</span>
            <Link
              to={`/courses/${course.slug}`}
              className={cn(
                'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                course.done ? 'text-[#69738A] hover:text-accent' : 'bg-accent/10 text-accent hover:bg-accent/20',
              )}
            >
              {course.done ? 'ดูคอร์ส' : 'เริ่มเรียน'}
            </Link>
          </li>
        ))}
      </ol>
    </div>
  )
}

function StatTile({ icon: Icon, label, value, total }: { icon: LucideIcon; label: string; value: number; total?: number }) {
  return (
    <div className="flex items-center gap-4 border border-[#E7E7ED] bg-white p-5" style={{ borderRadius: 6 }}>
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/10 text-accent"><Icon className="h-5 w-5" strokeWidth={1.7} /></span>
      <div>
        <p className="text-xs text-[#69738A]">{label}</p>
        <p className="text-2xl font-bold leading-tight tabular-nums text-[#19172A]">
          {value}{total !== undefined && <span className="text-base font-normal text-[#69738A]"> / {total}</span>}
        </p>
      </div>
    </div>
  )
}

export default function SkillsTab() {
  const [data, setData] = useState<MySkillsData | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [attempt, setAttempt] = useState(0)

  const retry = () => {
    setLoading(true)
    setError('')
    setAttempt((n) => n + 1)
  }

  useEffect(() => {
    let cancelled = false
    getMySkills()
      .then((d) => {
        if (cancelled) return
        setData(d)
        // เปิดรายการคอร์สของสกิลแรกที่กำลังเรียนอยู่ไว้ให้เลย
        const all = d.categories.flatMap((c) => c.skills)
        const inProgress = all.find((s) => s.done > 0 && !s.earned)
        setSelectedId((inProgress ?? all[0])?.skill_id ?? null)
      })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดสกิลไม่สำเร็จ') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [attempt])

  const categories = useMemo(() => data?.categories ?? [], [data])
  const summary = data?.summary

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-[84px] animate-pulse bg-[#F3F2FB]" style={{ borderRadius: 6 }} />)}
        </div>
        <div className="h-56 animate-pulse bg-[#F3F2FB]" style={{ borderRadius: 6 }} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="border border-[#E7E7ED] bg-white p-10 text-center" style={{ borderRadius: 6 }}>
        <p className="text-sm text-[#69738A]">{error}</p>
        <button type="button" onClick={retry} className="mt-4 rounded-full bg-accent px-5 py-2 text-sm font-medium text-white hover:opacity-90">
          ลองอีกครั้ง
        </button>
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div className="border border-[#E7E7ED] bg-white p-10 text-center" style={{ borderRadius: 6 }}>
        <Hexagon className="mx-auto h-10 w-10 text-[#C9C5E8]" strokeWidth={1.5} />
        <p className="mt-4 text-[15px] font-semibold text-[#19172A]">ยังไม่มีสกิลให้สะสม</p>
        <p className="mt-1 text-sm text-[#69738A]">เมื่อมีสกิลเปิดให้สะสม คุณจะเห็นความคืบหน้าของแต่ละสกิลที่นี่</p>
        <Link to="/courses" className="mt-5 inline-flex rounded-full bg-accent px-5 py-2 text-sm font-medium text-white hover:opacity-90">
          ดูคอร์สทั้งหมด
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile icon={Trophy} label="สกิลที่ได้รับแล้ว" value={summary?.earned ?? 0} total={summary?.skills ?? 0} />
        <StatTile icon={Hexagon} label="สกิลที่กำลังสะสม" value={summary?.in_progress ?? 0} />
        <StatTile icon={BookOpen} label="คอร์สที่เรียนจบ" value={summary?.done_courses ?? 0} total={summary?.all_courses ?? 0} />
      </div>

      <div className="space-y-6">
        {categories.map((category, index) => {
          const color = CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]
          const selected = category.skills.find((s) => s.skill_id === selectedId) ?? null

          return (
            <section key={category.category_id} className="border border-[#E7E7ED] bg-white p-6" style={{ borderRadius: 6 }}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-[17px] font-bold text-[#19172A]">{category.name}</h3>
                  <p className="mt-1 text-xs text-[#69738A]">
                    เรียนจบ {category.done} / {category.total} คอร์ส · ได้รับสกิล {category.earned_count} / {category.skills.length}
                  </p>
                </div>
                <Link
                  to={`/courses?cat=${category.category_id}`}
                  className="inline-flex items-center gap-1 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors hover:bg-accent/5"
                  style={{ borderColor: color.from, color: color.from }}
                >
                  ดูคอร์สในหมวดนี้ทั้งหมด <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <div className="mt-5 flex flex-wrap gap-x-2 gap-y-3">
                {category.skills.map((sub) => (
                  <SubSkillCard
                    key={sub.skill_id}
                    sub={sub}
                    category={color}
                    selected={sub.skill_id === selectedId}
                    onSelect={() => setSelectedId(sub.skill_id === selectedId ? null : sub.skill_id)}
                  />
                ))}
              </div>

              {selected && <SkillDetail sub={selected} category={color} />}
            </section>
          )
        })}
      </div>
    </div>
  )
}
